import { EventDto } from '../../models/dto';
import {
	calcCommunityProfileRoles,
	CalculatedCommunityRoles,
	CheckPermissionProps,
	getCommunityProfile,
} from './utils';

/**
 * CRUD operations around community:
 * read, update, delete
 */

export type CommunityPermissionProps = CheckPermissionProps;

export type CommunityPermissionOptions = object; // no additional props, for future use

export type HasCommunnityCreatePermissionOptions = CommunityPermissionOptions;
export function hasCommunityCreatePermission(
	{ user }: CommunityPermissionProps & HasCommunnityCreatePermissionOptions,
	_communityRoles: CalculatedCommunityRoles
) {
	return !!user && !user.isCommunityProfile;
}

export type HasCommunityEditPermissionOptions = CommunityPermissionOptions;
export function hasCommunityEditPermission(
	{ user, selectedCommunity }: CommunityPermissionProps & HasCommunityEditPermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !selectedCommunity) return false;

	const userIsOldCommunity = selectedCommunity && user && selectedCommunity.id === user.id && selectedCommunity.isCommunityProfile;

	const { isOwner } = communityRoles ?? {};

	return isOwner || userIsOldCommunity;
}

export type HasCampaignBlastReadPermissionOptions = CommunityPermissionOptions;
export function hasCampaignBlastReadPermission(
	{ user, selectedCommunity }: CommunityPermissionProps & HasCampaignBlastReadPermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !selectedCommunity) return false;

	const { isOwner, isLeader } = communityRoles ?? {};

	return isOwner || isLeader;
}

export type HasMembershipSettingsReadPermissionOptions = CommunityPermissionOptions;
export function hasMembershipSettingsReadPermission(
	{ user, selectedCommunity }: CommunityPermissionProps & HasMembershipSettingsReadPermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !selectedCommunity) return false;

	const isSimpleUserCommunity = selectedCommunity && user && selectedCommunity?.id === user?.id;

	const { isOwner } = communityRoles ?? {};

	return isSimpleUserCommunity || isOwner;
}

export type HasCommunityOverviewReadPermissionOptions = CommunityPermissionOptions;
export function hasCommunityOverviewReadPermission(
	{ user, selectedCommunity }: CommunityPermissionProps & HasCommunityOverviewReadPermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !selectedCommunity) return false;

	const { isOwner, isLeader } = communityRoles ?? {};

	return isOwner || isLeader;
}

export type HasCommunityOrdersReadPermissionOptions = CommunityPermissionOptions & {
	event?: EventDto;
};
export function hasCommunityOrdersReadPermission(
	{ user, selectedCommunity, event }: CommunityPermissionProps & HasCommunityOrdersReadPermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !selectedCommunity) return false;

	const isSimpleUserCommunity = selectedCommunity && user && selectedCommunity?.id === user?.id;

	const { isOwner, isLeader, isVolunteer } = communityRoles ?? {};

	const leaderPermission = isLeader;
	// volunteers can see tickets (orders) of single event
	const volunteerPermission = isVolunteer && !!event;

	return isSimpleUserCommunity || isOwner || leaderPermission || volunteerPermission;
}

export type HasCommunityWalletReadPermissionOptions = CommunityPermissionOptions;
export function hasCommunityWalletReadPermission(
	{ user, selectedCommunity }: CommunityPermissionProps & HasCommunityWalletReadPermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !selectedCommunity) return false;

	const { isOwner } = communityRoles ?? {};

	return isOwner;
}

export type HasCommunitySettingsReadPermissionOptions = CommunityPermissionOptions;
export function hasCommunitySettingsReadPermission(
	{ user, selectedCommunity }: CommunityPermissionProps & HasCommunitySettingsReadPermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !selectedCommunity) return false;

	const { isOwner, isLeader } = communityRoles ?? {};

	// only owners and leaders can read community roles settings
	return isOwner || isLeader;
}

export type HasCommunityIntegrationsReadPermissionOptions = CommunityPermissionOptions;
export function hasCommunityIntegrationsReadPermission(
	{ user, selectedCommunity }: CommunityPermissionProps & HasCommunityIntegrationsReadPermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !selectedCommunity) return false;

	const { isOwner } = communityRoles ?? {};

	// only owners can read community integrations settings
	return isOwner;
}

export type HasCommunityPerksSettingsReadPermissionOptions = CommunityPermissionOptions;
export function hasCommunityPerksSettingsReadPermission(
	{ user, selectedCommunity }: CommunityPermissionProps & HasCommunityPerksSettingsReadPermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !selectedCommunity) return false;

	const { isOwner, isLeader } = communityRoles ?? {};

	// only owners and leaders can read community perks settings
	return isOwner || isLeader;
}

export function getCommunityPermissions(props: CommunityPermissionProps) {
	if (!props.user || !props.selectedCommunity || !props.communityProfiles) return;

	const communityProfile = getCommunityProfile(props.communityProfiles, props.user.id, props.selectedCommunity.id);
	const communityRoles = calcCommunityProfileRoles(communityProfile);

	return {
		canCreate: (options: HasCommunnityCreatePermissionOptions) =>
			hasCommunityCreatePermission({ ...props, ...options }, communityRoles),
		canEdit: (options: HasCommunityEditPermissionOptions) =>
			hasCommunityEditPermission({ ...props, ...options }, communityRoles),
		canReadMembership: (options: HasMembershipSettingsReadPermissionOptions) =>
			hasMembershipSettingsReadPermission({ ...props, ...options }, communityRoles),
		canReadOverview: (options: HasCommunityOverviewReadPermissionOptions) =>
			hasCommunityOverviewReadPermission({ ...props, ...options }, communityRoles),
		canReadOrders: (options: HasCommunityOrdersReadPermissionOptions) =>
			hasCommunityOrdersReadPermission({ ...props, ...options }, communityRoles),
		canReadWallet: (options: HasCommunityWalletReadPermissionOptions) =>
			hasCommunityWalletReadPermission({ ...props, ...options }, communityRoles),
		canReadSettings: (options: HasCommunitySettingsReadPermissionOptions) =>
			hasCommunitySettingsReadPermission({ ...props, ...options }, communityRoles),
		canReadIntegrations: (options: HasCommunitySettingsReadPermissionOptions) =>
			hasCommunityIntegrationsReadPermission({ ...props, ...options }, communityRoles),
		canReadCampaignBlast: (options: HasCampaignBlastReadPermissionOptions) =>
			hasCampaignBlastReadPermission({ ...props, ...options }, communityRoles),
		canReadPerks: (options: HasCommunityPerksSettingsReadPermissionOptions) =>
			hasCommunityPerksSettingsReadPermission({ ...props, ...options }, communityRoles),
	};
}
